import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import {
  baseSalaryApi,
  F9420,
  DetailItem,
  CreateRequest,
  UpdateRequest,
} from '../../../services/baseSalaryApi';

export type Mode = 'browse' | 'add' | 'edit';

const emptyDetail = (): DetailItem => ({
  f9421_11: '',
  f9421_11a: '',
  f9421_12: 0,
  f9421_13: '',
});

export function useBaseSalaryForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F9420[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Header fields
  const [empID, setEmpID] = useState('');
  const [taxDeduction, setTaxDeduction] = useState(1);
  const [salaryMethod, setSalaryMethod] = useState(1);
  const [bank, setBank] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [payMethod, setPayMethod] = useState(1);
  const [dependents, setDependents] = useState(0);
  const [laborInsurance, setLaborInsurance] = useState(0);
  const [healthInsurance, setHealthInsurance] = useState(0);
  const [healthDependents, setHealthDependents] = useState(0);
  const [pensionSystem, setPensionSystem] = useState(1);
  const [voluntaryContrib, setVoluntaryContrib] = useState(2);
  const [contribPercent, setContribPercent] = useState(0);
  const [monthlyPension, setMonthlyPension] = useState(0);

  // Detail rows
  const [details, setDetails] = useState<DetailItem[]>([]);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await baseSalaryApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      syncFromRecord(current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  const syncFromRecord = (rec: F9420) => {
    setEmpID(rec.f9420_01);
    setTaxDeduction(rec.f9420_11);
    setSalaryMethod(rec.f9420_12);
    setBank(rec.f9420_13);
    setBankAccount(rec.f9420_14);
    setPayMethod(rec.f9420_15);
    setDependents(rec.f9420_17);
    setLaborInsurance(rec.f9420_21);
    setHealthInsurance(rec.f9420_23);
    setHealthDependents(rec.f9420_24);
    setPensionSystem(rec.f9420_34);
    setVoluntaryContrib(rec.f9420_31);
    setContribPercent(rec.f9420_37);
    setMonthlyPension(rec.f9420_38);
    setDetails(
      (rec.details || []).map(d => ({
        f9421_11: d.f9421_11,
        f9421_11a: d.f9421_11a,
        f9421_12: d.f9421_12,
        f9421_13: d.f9421_13,
      }))
    );
  };

  const resetFields = () => {
    setEmpID('');
    setTaxDeduction(1);
    setSalaryMethod(1);
    setBank('');
    setBankAccount('');
    setPayMethod(1);
    setDependents(0);
    setLaborInsurance(0);
    setHealthInsurance(0);
    setHealthDependents(0);
    setPensionSystem(1);
    setVoluntaryContrib(2);
    setContribPercent(0);
    setMonthlyPension(0);
    setDetails([]);
  };

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    resetFields();
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    syncFromRecord(current);
  };

  // Cancel
  const cancel = () => {
    setMode('browse');
    if (current) syncFromRecord(current);
  };

  // Add detail row (BR-DET01, max 99)
  const addDetail = () => {
    if (details.length >= 99) {
      message.warning('薪資明細最多99筆!');
      return;
    }
    setDetails([...details, emptyDetail()]);
  };

  // Remove detail row (BR-DET02)
  const removeDetail = (index: number) => {
    setDetails(details.filter((_, i) => i !== index));
  };

  // Update a detail field
  const updateDetail = (index: number, field: keyof DetailItem, value: string | number) => {
    setDetails(prev => prev.map((d, i) => i === index ? { ...d, [field]: value } : d));
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        const req: CreateRequest = {
          f9420_01: empID,
          f9420_11: taxDeduction,
          f9420_12: salaryMethod,
          f9420_13: bank,
          f9420_14: bankAccount,
          f9420_15: payMethod,
          f9420_17: dependents,
          f9420_21: laborInsurance,
          f9420_23: healthInsurance,
          f9420_24: healthDependents,
          f9420_31: voluntaryContrib,
          f9420_34: pensionSystem,
          f9420_37: contribPercent,
          f9420_38: monthlyPension,
          details,
        };
        await baseSalaryApi.create(req);
        message.success('新增成功');
      } else {
        const req: UpdateRequest = {
          f9420_11: taxDeduction,
          f9420_12: salaryMethod,
          f9420_13: bank,
          f9420_14: bankAccount,
          f9420_15: payMethod,
          f9420_17: dependents,
          f9420_21: laborInsurance,
          f9420_23: healthInsurance,
          f9420_24: healthDependents,
          f9420_31: voluntaryContrib,
          f9420_34: pensionSystem,
          f9420_37: contribPercent,
          f9420_38: monthlyPension,
          details,
        };
        await baseSalaryApi.update(current!.f9420_01, req);
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetID = mode === 'add' ? empID : current!.f9420_01;
      const idx = data.findIndex(r => r.f9420_01 === targetID);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await baseSalaryApi.delete(current.f9420_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (text: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f9420_01 >= text);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    // Header fields
    empID, setEmpID,
    taxDeduction, setTaxDeduction,
    salaryMethod, setSalaryMethod,
    bank, setBank,
    bankAccount, setBankAccount,
    payMethod, setPayMethod,
    dependents, setDependents,
    laborInsurance, setLaborInsurance,
    healthInsurance, setHealthInsurance,
    healthDependents, setHealthDependents,
    pensionSystem, setPensionSystem,
    voluntaryContrib, setVoluntaryContrib,
    contribPercent, setContribPercent,
    monthlyPension, setMonthlyPension,
    // Details
    details, addDetail, removeDetail, updateDetail,
    // Navigation
    navFirst, navPrev, navNext, navLast,
    // Actions
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  };
}
